import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { REST_API_URL } from "../api/config";
import { buildHeaders } from "../api/buildHeaders";
import { ModalPortal } from "./ModalPortal";

const BASE_URL = REST_API_URL;

const AI_LANGUAGES = [
  { code: "en", label: "English - Inglés", flag: "🇺🇸" },
  { code: "es", label: "Spanish - Español", flag: "🇪🇸" },
];

export function AILanguageModal({ isOpen, onClose, initialLanguages = [] }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const codes = initialLanguages.map((l) =>
        typeof l === "string" ? l : l?.name || l?.code || ""
      ).filter(Boolean);
      setSelected(codes);
    }
  }, [isOpen, initialLanguages]);

  const toggle = (code) => {
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      toast.error(t("settings.aiLanguageDesc"));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/auth/add-language/`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({ languages: selected }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data?.detail || data?.message || "Error");
        return;
      }
      toast.success(t("settings.aiLanguageSaved"));
      onClose();
    } catch (err) {
      console.error("Failed to update AI languages:", err);
      toast.error(t("settings.aiLanguageSaved"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalPortal>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="ai-lang-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-end sm:items-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            key="ai-lang-modal"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 text-center mb-1">
                {t("settings.aiLanguageTitle")}
              </h3>
              <p className="text-sm text-gray-500 text-center mb-5">
                {t("settings.aiLanguageDesc")}
              </p>

              <div className="flex flex-col gap-2 mb-5">
                {AI_LANGUAGES.map((lang) => {
                  const isActive = selected.includes(lang.code);
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => toggle(lang.code)}
                      className={`flex items-center justify-between w-full px-4 py-3.5 rounded-2xl border-2 transition-all ${
                        isActive
                          ? "border-[#020637] bg-[#020637]/5"
                          : "border-gray-200 hover:border-[#020637]/40 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl leading-none">{lang.flag}</span>
                        <span
                          className={`text-sm font-medium ${
                            isActive ? "text-[#020637]" : "text-gray-700"
                          }`}
                        >
                          {lang.label}
                        </span>
                      </div>

                      {/* Checkbox style */}
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          isActive
                            ? "border-[#020637] bg-[#020637]"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        {isActive && (
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                          >
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="white"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={handleSave}
                disabled={loading || selected.length === 0}
                className="w-full py-3.5 rounded-2xl bg-[#020637] hover:bg-[#0e1c7a] active:bg-[#060e4a] text-white font-semibold text-sm transition-colors disabled:opacity-60"
              >
                {loading ? t("settings.saving") : t("settings.save")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </ModalPortal>
  );
}
